import {
  Alignment,
  FloatingFocusManager,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useTransitionStyles,
  UseTransitionStylesProps,
} from "@floating-ui/react";
import {
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  useToasts,
  ToastContext,
  useToast,
  getToastPosition,
  useTimeout,
} from "./useToast.shared";
import { zIndexLayer } from "@sellar-io/components";

//taken from https://github.com/floating-ui/floating-ui/pull/2279

type ToastPlacement =
  | "top"
  | "bottom"
  | `top-${Alignment}`
  | `bottom-${Alignment}`;

type Id = string;
type ToastType = {
  id: Id;
  delay?: number;
  autoClose?: boolean;
  requestClose?: boolean;
  transition?: UseTransitionStylesProps;
  placement?: ToastPlacement;
  render?: (id: Id, onClose?: () => void) => ReactNode;
};
interface ProviderType extends Partial<ToastType> {
  children: ReactNode;
}

const DEFAULT_TRANSITION: UseTransitionStylesProps = {
  duration: 300,
  initial: () => ({
    opacity: 0,
    maxHeight: 0,
  }),
  open: ({ side }) => ({
    opacity: 1,
    transform: {
      top: "translateY(-0.5rem)",
      right: "translateX(0.5rem)",
      bottom: "translateY(0.5rem)",
      left: "translateX(-0.5rem)",
    }[side],
  }),
  close: ({ side }) => ({
    opacity: 0,
    maxHeight: 0,
    transform: {
      top: "translateY(0.5rem)",
      right: "translateX(-0.5rem)",
      bottom: "translateY(-0.5rem)",
      left: "translateX(0.5rem)",
    }[side],
  }),
};
const DEFAULT_RENDER = () => null;

// memo: Default value is kept by provider.
// This is useful when maintaining global values in the provider,
// and is also useful when applying default values to individual toasts.
export function ToastProvider({
  placement: defaultPlacement = "bottom",
  delay = 3000,
  autoClose = true,
  requestClose = false,
  transition = DEFAULT_TRANSITION,
  render = DEFAULT_RENDER,
  children,
}: ProviderType) {
  const context = useToasts();
  const { getFloatingProps } = useInteractions();

  // add default toast placement to placements Set
  const placements = context.placements.add(defaultPlacement);

  return (
    <ToastContext.Provider value={context}>
      {Array.from(placements).map((placement) => {
        const toasts = context.toasts
          .map((toast) => {
            if (!toast.placement) {
              // if placement undefined, set default placement
              return { ...toast, placement: defaultPlacement };
            }
            return toast;
          })
          .filter((toast) => toast.placement === placement);

        return (
          <ul
            key={placement}
            aria-live="polite"
            style={{
              position: "fixed",
              pointerEvents: "none",
              padding: 0,
              display: "flex",
              flexDirection: "column",
              zIndex: zIndexLayer.toast,
              ...getToastPosition(placement),
            }}
            {...getFloatingProps({
              role: "region",
            })}
          >
            {toasts.map((toast, index) => (
              <ToastContent
                key={toast.id}
                id={toast.id}
                placement={toast.placement ?? placement}
                autoClose={toast.autoClose ?? autoClose}
                delay={toast.delay ?? delay}
                transition={
                  toast.transition
                    ? { ...transition, ...toast.transition }
                    : transition
                }
                ref={(node) => {
                  context.listRef.current[index] = node;
                }}
                requestClose={toast.requestClose ?? requestClose}
                render={toast.render ?? render}
              ></ToastContent>
            ))}
          </ul>
        );
      })}
      {children}
    </ToastContext.Provider>
  );
}

// Pauses closing the toast when the mouse is over the toast

type ToastContentProps = Required<ToastType> & HTMLAttributes<HTMLLIElement>;

export const ToastContent = forwardRef<HTMLLIElement, ToastContentProps>(
  ({ id, delay, transition, autoClose, requestClose, render }, propRef) => {
    const [open, setOpen] = useState(true);
    const [focus, setFocus] = useState(false);
    const isOpen = open && !requestClose;
    const { context, refs } = useFloating({
      open: isOpen,
      onOpenChange: setOpen,
      nodeId: id,
    });
    const ref = useMergeRefs([propRef, refs.setFloating]);
    const dismiss = useDismiss(context, {
      enabled: focus,
      outsidePress: false,
    });
    const { close } = useToast();
    const { getFloatingProps, getItemProps } = useInteractions([dismiss]);
    const { isMounted, styles } = useTransitionStyles(context, transition);

    const closeToastTime = useMemo(() => {
      if (transition.duration == null) {
        // default duration
        return 300;
      }
      if (typeof transition.duration === "number") {
        return transition.duration;
      }
      return transition.duration.close ?? 300;
    }, [transition.duration]);

    const closeToast = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        close(id);
      }, closeToastTime);
    }, [close, closeToastTime, id]);

    const { start, stop } = useTimeout(
      autoClose ? closeToast : () => {},
      delay,
    );

    return isMounted ? (
      <FloatingFocusManager
        context={context}
        modal={false}
        order={["floating"]}
        initialFocus={-1}
      >
        <li
          ref={ref}
          role="status"
          aria-atomic="true"
          aria-hidden="false"
          tabIndex={0}
          style={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "100vh",
            alignItems: "center",
            ...styles,
          }}
          {...getFloatingProps()}
          {...getItemProps({
            onFocus: () => {
              setFocus(true);
              stop();
            },
            onBlur: () => {
              setFocus(false);
              start();
            },
            onMouseOver: () => {
              stop();
            },
            onMouseLeave: () => {
              start();
            },
          })}
        >
          <div style={{ pointerEvents: "auto", width: "fit-content" }}>
            {render && render(id, () => closeToast())}
          </div>
        </li>
      </FloatingFocusManager>
    ) : null;
  },
);

ToastContent.displayName = "ToastContent";
